import { addDays, addMonths } from '@if/core/date'
import { EProductsInsurance } from '@if/core/types'
import { ControlledStringDateOnlyPicker } from '@if/mui'
import { InputAdornment, Stack } from '@mui/material'
import { InputTextField } from '@src/components'
import { appSelectors } from '@src/data/store/AppStore'
import { ProductsWorksheetDto } from '@src/data/types/ProductsWorksheetSchema'
import { useEffect, useMemo } from 'react'
import { Control, FieldErrors, UseFormRegister, UseFormSetValue, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

interface Props {
  register: UseFormRegister<ProductsWorksheetDto>
  insurance: EProductsInsurance
  errors: FieldErrors<ProductsWorksheetDto>
  control: Control<ProductsWorksheetDto>
  setValue: UseFormSetValue<ProductsWorksheetDto>
  today: Date
}

const ProductsInsuranceDetails = ({ insurance: name, register, errors, control, setValue, today }: Props) => {
  const { t } = useTranslation()
  const featureFlags = useSelector(appSelectors.getFeatureFlags)
  const showAdditionalSummaryVariables = featureFlags.showAdditionalSummaryVariables
  const showDatePicker = name !== EProductsInsurance.ExtendedWarranty
  const term = useWatch({
    control,
    name: `${name}.term`,
  })
  const maxDate = useMemo(() => {
    return addDays(addMonths(today, isNaN(Number(term)) ? 12 : Number(term)), 21)
  }, [today, term])

  const minDate = useMemo(() => {
    return isNaN(Number(term)) ? today : addMonths(today, Number(term))
  }, [today, term])

  useEffect(() => {
    if (showDatePicker && term) {
      const termNumber = Number(term)
      if (!isNaN(termNumber)) {
        const calculatedEndDate = addMonths(today, termNumber)
        const calculatedEndDateAsString = calculatedEndDate.toISOString().split('T')[0]
        setValue(`${name}.endDate`, calculatedEndDateAsString, { shouldValidate: true })
      }
    }
  }, [term, showDatePicker, today, name, setValue])

  return (
    <Stack spacing={2} mt={2}>
      <InputTextField
        label={t('worksheet.policy')}
        fullWidth
        {...register(`${name}.police`)}
        error={errors[name]?.police}
      />
      <InputTextField
        label={t('worksheet.term')}
        error={errors[name]?.term}
        {...register(`${name}.term`)}
        slotProps={{
          input: {
            endAdornment: <InputAdornment position="end">{t('worksheet.month')}</InputAdornment>,
          },
        }}
      />
      {showAdditionalSummaryVariables && showDatePicker && (
        <ControlledStringDateOnlyPicker
          name={`${name}.endDate`}
          control={control}
          label={t('worksheet.productInsuranceEndDate')}
          minDate={minDate}
          maxDate={maxDate}
          disabled={isNaN(Number(term)) || !term}
        />
      )}
      <InputTextField
        label={t('worksheet.premium')}
        error={errors[name]?.amount}
        {...register(`${name}.amount`)}
        slotProps={{
          input: {
            endAdornment: <InputAdornment position="end">$</InputAdornment>,
          },
        }}
      />
    </Stack>
  )
}

export default ProductsInsuranceDetails
